/**
 * Do My Chats Look Fat? — UI Application
 *
 * Handles file upload, progress display, and result rendering.
 * All analysis is delegated to the engine (engine/index.js).
 */

import { analyzeArchive } from './engine/index.js';

// ============================================================
// DOM References
// ============================================================

const dropZone = document.getElementById('dropZone');
const fileInput = document.getElementById('fileInput');
const progressFill = document.getElementById('progressFill');
const progressLabel = document.getElementById('progressLabel');
const resultsContainer = document.getElementById('results-container');
const resetBtn = document.getElementById('resetBtn');
const errorToast = document.getElementById('error-toast');
const errorMessage = document.getElementById('errorMessage');
const errorDismiss = document.getElementById('errorDismiss');

// ============================================================
// State Management
// ============================================================

function setState(state) {
  document.body.dataset.state = state;
  if (state === 'results') {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }
}

// ============================================================
// Upload Handling
// ============================================================

dropZone.addEventListener('click', () => fileInput.click());

dropZone.addEventListener('dragover', (e) => {
  e.preventDefault();
  dropZone.classList.add('dragover');
});

dropZone.addEventListener('dragleave', () => {
  dropZone.classList.remove('dragover');
});

dropZone.addEventListener('drop', (e) => {
  e.preventDefault();
  dropZone.classList.remove('dragover');
  if (e.dataTransfer.files.length) handleFile(e.dataTransfer.files[0]);
});

fileInput.addEventListener('change', () => {
  if (fileInput.files.length) handleFile(fileInput.files[0]);
});

resetBtn.addEventListener('click', () => {
  fileInput.value = '';
  resultsContainer.innerHTML = '';
  setState('upload');
});

errorDismiss.addEventListener('click', () => {
  errorToast.hidden = true;
});

async function handleFile(file) {
  setState('processing');
  progressFill.style.width = '0%';
  progressLabel.textContent = 'Starting...';

  try {
    const result = await analyzeArchive(file, {
      onProgress: (e) => {
        // Map stages to overall percentage
        let overall = 0;
        if (e.stage === 'unzip') overall = e.pct * 0.3;
        else if (e.stage === 'parse') overall = 30 + e.pct * 0.2;
        else if (e.stage === 'analyze') overall = 50 + e.pct * 0.5;

        progressFill.style.width = `${Math.round(overall)}%`;
        const label = e.detail ? `${e.detail}` : `${e.stage} ${e.pct}%`;
        progressLabel.textContent = label;
      },
    });

    renderResults(result);
    setState('results');
  } catch (err) {
    setState('upload');
    errorMessage.textContent = err.message;
    errorToast.hidden = false;
  }
}

// ============================================================
// Results Rendering
// ============================================================

function renderResults(result) {
  resultsContainer.innerHTML = [
    renderChatGPTTax(result),
    renderBloatCheck(result),
    renderContextXray(result),
    renderFattestChats(result),
    renderGhostWaste(result),
    renderPatterns(result),
    renderPerceivedWait(result),
    renderOverview(result),
    renderRawData(result),
  ].join('');

  initAnimations();
  initTaxBreakdown();
  initWashBreakdown();
}

// ============================================================
// Section: ChatGPT Tax (Hero)
// ============================================================

function renderChatGPTTax(result) {
  const { taxPaid, projected, archiveSpanDays } = result.chatgptTax;

  // Three-loss breakdown proportions
  const total = taxPaid.totalMinutes || 1;
  const waitingPct = (taxPaid.waitingLossMinutes / total * 100).toFixed(1);
  const cognitivePct = (taxPaid.cognitiveLossMinutes / total * 100).toFixed(1);
  const wastePct = ((taxPaid.contextWasteMinutes || 0) / total * 100).toFixed(1);

  // Waiting panel stats
  const convsWithRender = result.conversations.filter(c =>
    c.turnPairs.some(tp => tp.perceivedWait)
  ).length;
  const perConvMedians = result.conversations
    .map(c => {
      const waits = c.turnPairs
        .filter(tp => tp.perceivedWait)
        .map(tp => tp.perceivedWait.total);
      return waits.length > 0 ? medianOf([...waits].sort((a, b) => a - b)) : null;
    })
    .filter(m => m !== null)
    .sort((a, b) => a - b);
  const archiveMedianWait = perConvMedians.length > 0
    ? medianOf(perConvMedians)
    : null;
  const medianWaitStr = archiveMedianWait !== null
    ? `${archiveMedianWait.toFixed(1)}s`
    : '\u2014';

  // Context waste panel
  const wasteEmpty = !taxPaid.contextWasteCount;

  return `
    <section class="section" id="section-tax">
      <div class="section-label">The ChatGPT Tax</div>

      <div class="metric-hero">
        <div class="metric-value" data-counter="${taxPaid.totalHours}">0</div>
        <div class="metric-label">hours lost to ChatGPT inefficiency</div>
        <div class="metric-detail">over ${Math.round(archiveSpanDays)} days of usage</div>
      </div>

      <div class="tax-bar-wrap">
        <div class="tax-bar">
          <div class="tax-bar-seg" data-tax-type="waiting" style="width:${waitingPct}%;background:var(--denim)">${parseFloat(waitingPct) > 8 ? waitingPct + '%' : ''}</div>
          <div class="tax-bar-seg" data-tax-type="cognitive" style="width:${cognitivePct}%;background:var(--accent)">${parseFloat(cognitivePct) > 8 ? cognitivePct + '%' : ''}</div>
          <div class="tax-bar-seg" data-tax-type="waste" style="width:${wastePct}%;background:var(--red)">${parseFloat(wastePct) > 8 ? wastePct + '%' : ''}</div>
        </div>
        <div class="tax-legend">
          <div class="tax-legend-item" data-tax-type="waiting">
            <span class="dot" style="background:var(--denim)"></span>
            The Drag: ${fmtMin(taxPaid.waitingLossMinutes)}
          </div>
          <div class="tax-legend-item" data-tax-type="cognitive">
            <span class="dot" style="background:var(--accent)"></span>
            Stretch Marks: ${fmtMin(taxPaid.cognitiveLossMinutes)}
          </div>
          <div class="tax-legend-item" data-tax-type="waste">
            <span class="dot" style="background:var(--red)"></span>
            Spare Tire: ${wasteEmpty ? '\u2014' : fmtMin(taxPaid.contextWasteMinutes)}
          </div>
        </div>

        <div class="tax-detail-panel" data-tax-panel="waiting" hidden>
          <p class="tax-explain">Time spent waiting for responses to appear in your browser. As conversations grow, each new response takes longer to render &mdash; not because the model is thinking longer, but because your browser is redrawing more and more content each time.</p>
          <div class="metric-grid">
            <div class="metric-card">
              <div class="metric-label">Total waiting</div>
              <div class="metric-value">${fmtTimeHtml(taxPaid.waitingLossMinutes)}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Chats with render data</div>
              <div class="metric-value">${convsWithRender}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Median delay per response</div>
              <div class="metric-value">${medianWaitStr}</div>
            </div>
          </div>
          <div class="tax-contributors">
            <div class="section-label">What contributes</div>
            <ul>
              <li>Long conversations with high message counts</li>
              <li>Code-heavy responses (large DOM elements)</li>
              <li>Conversations with many tool call outputs</li>
            </ul>
          </div>
        </div>

        <div class="tax-detail-panel" data-tax-panel="cognitive" hidden>
          <p class="tax-explain">Time <em>you</em> spend compensating when ChatGPT's memory fades. As a conversation grows, the model's grip on earlier context weakens &mdash; so you repeat instructions it should already know, or give up and start a fresh chat on the same topic, re-establishing context from scratch each time.</p>
          <div class="metric-grid">
            <div class="metric-card">
              <div class="metric-label">Re-instructions</div>
              <div class="metric-value">${taxPaid.reinstructionCount}<span class="unit">instances</span></div>
              <div class="metric-detail">${fmtMin(taxPaid.reinstructionMinutes)}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Topic restarts</div>
              <div class="metric-value">${taxPaid.topicRestartExtraConversations}<span class="unit">extra chats</span></div>
              <div class="metric-detail">${fmtMin(taxPaid.topicRestartMinutes)}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Combined</div>
              <div class="metric-value">${fmtTimeHtml(taxPaid.cognitiveLossMinutes)}</div>
            </div>
          </div>
          <div class="tax-contributors">
            <div class="section-label">What contributes</div>
            <ul>
              <li>Long conversations where the model loses track of earlier instructions</li>
              <li>Not using Custom Instructions for recurring context</li>
              <li>Starting new chats instead of continuing existing ones or using Projects</li>
            </ul>
          </div>
        </div>

        <div class="tax-detail-panel" data-tax-panel="waste" hidden>
          ${wasteEmpty ? `
          <p class="tax-explain">No messages landed in the ghost zone. Your conversations stayed within the model's context window.</p>
          ` : `
          <p class="tax-explain">Messages you sent into the <strong>ghost zone</strong> &mdash; the point in a conversation where it has exceeded the context window and ChatGPT literally cannot see your earlier messages anymore. Unlike Stretch Marks (where the model is struggling but still trying), these messages were never processed at all. ChatGPT keeps responding as if everything is fine, so you don't realize it's lost the thread.</p>
          <div class="metric-grid">
            <div class="metric-card">
              <div class="metric-label">Ghost zone messages</div>
              <div class="metric-value">${taxPaid.contextWasteCount}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Time lost</div>
              <div class="metric-value">${fmtTimeHtml(taxPaid.contextWasteMinutes)}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Chats in the ghost zone</div>
              <div class="metric-value">${result.ghostZoneConversations}</div>
            </div>
          </div>
          <div class="tax-contributors">
            <div class="section-label">What contributes</div>
            <ul>
              <li>Very long conversations that blow past the context window</li>
              <li>Hidden token consumers: tool outputs, system prompts, and images eating context you can't see</li>
              <li>Continuing to add to chats well past the overflow point</li>
            </ul>
          </div>
          `}
        </div>
      </div>

      <div class="section-label" style="margin-top:2.5rem">At your current pace</div>
      <div class="metric-grid">
        <div class="metric-card">
          <div class="metric-label">This week</div>
          <div class="metric-value">${fmtTimeHtml(projected.weekMinutes)}</div>
          <div class="metric-detail">${projected.dailyRate.toFixed(1)} min/day</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">This month</div>
          <div class="metric-value">${fmtTimeHtml(projected.monthMinutes)}</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">This year</div>
          <div class="metric-value">${fmtTimeHtml(projected.yearMinutes)}</div>
        </div>
      </div>

      <div class="metric-detail" style="margin-top:0.75rem;text-align:center">
        Based on last ${projected.recentWindowDays} days:
        ${fmtMin(projected.recentRenderMinutes)} drag +
        ${fmtMin(projected.recentReinstructionMinutes + projected.recentRestartMinutes)} stretch marks +
        ${fmtMin(projected.recentContextWasteMinutes || 0)} spare tire
      </div>
    </section>
  `;
}

// ============================================================
// Tax Breakdown Accordion
// ============================================================

function initTaxBreakdown() {
  const wrap = document.querySelector('.tax-bar-wrap');
  if (!wrap) return;

  wrap.addEventListener('click', (e) => {
    const target = e.target.closest('[data-tax-type]');
    if (!target) return;
    const type = target.dataset.taxType;
    toggleTaxPanel(wrap, type);
  });
}

function toggleTaxPanel(wrap, type) {
  const panel = wrap.querySelector(`[data-tax-panel="${type}"]`);
  if (!panel) return;

  const isOpen = !panel.hidden;

  // Close all panels and remove active states
  wrap.querySelectorAll('.tax-detail-panel').forEach(p => p.hidden = true);
  wrap.querySelectorAll('.tax-bar-seg').forEach(s => s.classList.remove('active'));
  wrap.querySelectorAll('.tax-legend-item').forEach(l => l.classList.remove('active'));

  // If it was closed, open it
  if (!isOpen) {
    panel.hidden = false;
    wrap.querySelectorAll(`[data-tax-type="${type}"]`).forEach(el => el.classList.add('active'));
  }
}

// ============================================================
// Wash Breakdown Accordion (Weigh-In)
// ============================================================

function initWashBreakdown() {
  const wrap = document.querySelector('.bloat-bar-wrap');
  if (!wrap) return;

  // Attach click directly to each clickable element
  wrap.querySelectorAll('[data-wash-type]').forEach(el => {
    el.addEventListener('click', () => {
      toggleWashPanel(wrap, el.dataset.washType);
    });
  });
}

function toggleWashPanel(wrap, type) {
  const panel = wrap.querySelector(`[data-wash-panel="${type}"]`);
  if (!panel) return;

  const isOpen = panel.style.display !== 'none';

  wrap.querySelectorAll('.wash-detail-panel').forEach(p => p.style.display = 'none');
  wrap.querySelectorAll('.bloat-seg').forEach(s => s.classList.remove('active'));
  wrap.querySelectorAll('.bloat-legend span[data-wash-type]').forEach(l => l.classList.remove('active'));

  if (!isOpen) {
    panel.style.display = 'block';
    wrap.querySelectorAll(`[data-wash-type="${type}"]`).forEach(el => el.classList.add('active'));
  }
}

// ============================================================
// Section: Bloat Check
// ============================================================

function renderBloatCheck(result) {
  const total = result.totalConversations || 1;
  const ghostPct = ((result.ghostZoneConversations / total) * 100).toFixed(0);

  // Jeans score thresholds — Blown Out is anything above 1.0
  const wash = [0, 0, 0, 0, 0];
  for (const c of result.conversations) {
    const s = c.jeansScore ? c.jeansScore.score : 0;
    if (s <= 0.35) wash[0]++;
    else if (s <= 0.55) wash[1]++;
    else if (s <= 0.75) wash[2]++;
    else if (s <= 1.0) wash[3]++;
    else wash[4]++;
  }

  const washPct = wash.map(n => (n / total * 100).toFixed(1));
  const washNames = ['Loose Fit', 'Snug', 'Straining', 'Splitting', 'Blown Out'];
  const washKeys = ['loose-fit', 'snug', 'straining', 'splitting', 'blown-out'];
  const washRanges = ['0\u20130.35', '0.35\u20130.55', '0.55\u20130.75', '0.75\u20131.0', '1.0+'];
  const washDescs = [
    'Plenty of room. Next message will render fast, low risk of re-instruction or overflow.',
    'Starting to feel it. Some render lag or mild cognitive overhead on the next turn.',
    'Starting to feel painful. Noticeable wait on responses, re-instruction creeping in, or context getting tight.',
    'Clearly noticeable. Sluggish responses, repeated re-explaining, or context window under real pressure.',
    'Painful. Heavy render delay, high re-instruction cost, and context overflowed \u2014 this chat needs a fresh start.',
  ];

  const fatCount = wash[2] + wash[3] + wash[4];
  const fatStartPct = parseFloat(washPct[0]) + parseFloat(washPct[1]);

  return `
    <section class="section" id="section-bloat">
      <div class="section-label">The Weigh-In</div>
      <div class="section-title">${fatCount} of ${result.totalConversations} chats are fat</div>
      <div class="section-desc">
        How your next message will feel. Combines render drag, re-instruction risk,
        and context overflow into a single fit score.
      </div>

      <div class="bloat-bar-wrap" style="position:relative">
        <div class="bloat-bar">
          ${wash.map((n, i) => `<div class="bloat-seg wash-${i + 1}" data-wash-type="${washKeys[i]}" style="width:${washPct[i]}%">${n > 0 ? n : ''}</div>`).join('')}
        </div>
        ${fatCount > 0 ? `<div class="bloat-bar-fat-marker" style="left:${fatStartPct.toFixed(1)}%">
          <div class="bloat-bar-fat-line"></div>
          <div class="bloat-bar-fat-label">${fatCount} fat \u2192</div>
        </div>` : ''}
        <div class="bloat-legend">
          ${wash.map((n, i) => `<span data-wash-type="${washKeys[i]}"><span class="legend-dot wash-${i + 1}"></span> ${washNames[i]} (${n})</span>`).join('')}
        </div>

        ${washKeys.map((key, i) => `<div class="wash-detail-panel" data-wash-panel="${key}" style="display:none"><p class="wash-explain"><strong>${washNames[i]}</strong> (${washRanges[i]}) \u2014 ${washDescs[i]}</p></div>`).join('')}
      </div>

      <div class="stats-row">
        <div>
          <span class="stat-value">${result.ghostZoneConversations}</span>
          <span class="stat-label">Ghost zone chats</span>
        </div>
        <div>
          <span class="stat-value">${ghostPct}%</span>
          <span class="stat-label">of all chats</span>
        </div>
      </div>

      <div class="jeans-placeholder">
        Jeans card &mdash; static images coming soon
      </div>
    </section>
  `;
}

// ============================================================
// Section: Context X-Ray
// ============================================================

function renderContextXray(result) {
  let totalTextTokens = 0, totalImageTokens = 0, totalToolTokens = 0, totalSystemTokens = 0;
  let totalCodeBlocks = 0, totalCodeChars = 0;
  let totalTables = 0, totalTableChars = 0;
  let totalToolCalls = 0;
  let totalGhostWasted = 0;

  for (const c of result.conversations) {
    totalTextTokens += c.adjustedTokens.textTokens;
    totalImageTokens += c.adjustedTokens.imageTokens;
    totalToolTokens += c.adjustedTokens.toolCallTokens;
    totalSystemTokens += c.adjustedTokens.systemTokens;
    totalCodeBlocks += c.contentProfile.codeBlockCount;
    totalCodeChars += c.contentProfile.codeBlockChars;
    totalTables += c.contentProfile.tableCount;
    totalTableChars += c.contentProfile.tableChars;
    totalToolCalls += c.contentProfile.toolCallCount;
    totalGhostWasted += c.ghostWastedMessages || 0;
  }

  // Break text tokens into plain vs code vs table sub-segments
  const codeTokens = Math.ceil(totalCodeChars / 4);
  const tableTokens = Math.ceil(totalTableChars / 4);
  const plainTextTokens = Math.max(0, totalTextTokens - codeTokens - tableTokens);

  const grandTotal = totalTextTokens + totalImageTokens + totalToolTokens + totalSystemTokens;
  const invisibleTokens = totalToolTokens + totalSystemTokens;
  const invisiblePct = grandTotal > 0 ? (invisibleTokens / grandTotal * 100) : 0;

  const plainPct = grandTotal > 0 ? (plainTextTokens / grandTotal * 100) : 0;
  const codePct = grandTotal > 0 ? (codeTokens / grandTotal * 100) : 0;
  const tablePct = grandTotal > 0 ? (tableTokens / grandTotal * 100) : 0;
  const imagePct = grandTotal > 0 ? (totalImageTokens / grandTotal * 100) : 0;
  const toolPct = grandTotal > 0 ? (totalToolTokens / grandTotal * 100) : 0;
  const systemPct = grandTotal > 0 ? (totalSystemTokens / grandTotal * 100) : 0;

  // Count conversations by dominant content type
  const dominantCounts = { text: 0, code: 0, table: 0, image: 0 };
  for (const c of result.conversations) {
    const dt = c.renderProfile?.dominantContentType || 'text';
    dominantCounts[dt] = (dominantCounts[dt] || 0) + 1;
  }

  // Conversations where hidden context materially increases bloat
  const bloatDeltaConvs = result.conversations
    .filter(c => c.adjustedBloatScore - c.bloatScore > 0.05).length;

  // Top 5 biggest hidden bloat deltas
  const biggestHidden = result.conversations
    .filter(c => c.adjustedBloatScore - c.bloatScore > 0.05)
    .sort((a, b) => (b.adjustedBloatScore - b.bloatScore) - (a.adjustedBloatScore - a.bloatScore))
    .slice(0, 5);

  return `
    <section class="section" id="section-xray">
      <div class="section-label">Context X-Ray</div>
      <div class="section-title">What's really eating your context</div>
      <div class="section-desc">
        Your conversations contain more than just your messages. Tool outputs, system prompts,
        and image tokens silently consume context &mdash; pushing you into the ghost zone faster.
      </div>

      ${invisiblePct > 0.5 ? `
      <div class="xray-callout">
        <div class="xray-callout-value">${invisiblePct.toFixed(1)}%</div>
        <div class="xray-callout-label">of your total context is invisible overhead</div>
        <div class="xray-callout-detail">${fmtCompact(invisibleTokens)} tokens you never see &mdash; system prompts, browsing results, code execution output</div>
      </div>
      ` : ''}

      <div class="xray-bar-wrap">
        <div class="section-label" style="margin-bottom:0.5rem">Token composition across all chats</div>
        <div class="xray-bar">
          <div class="xray-seg" title="Plain text: ${plainPct.toFixed(1)}%" style="width:${plainPct.toFixed(1)}%;background:var(--text-secondary)"></div>
          ${codePct > 0.1 ? `<div class="xray-seg" title="Code: ${codePct.toFixed(1)}%" style="width:${codePct.toFixed(1)}%;background:var(--cyan)"></div>` : ''}
          ${tablePct > 0.1 ? `<div class="xray-seg" title="Tables: ${tablePct.toFixed(1)}%" style="width:${tablePct.toFixed(1)}%;background:var(--yellow)"></div>` : ''}
          ${imagePct > 0.1 ? `<div class="xray-seg" title="Images: ${imagePct.toFixed(1)}%" style="width:${imagePct.toFixed(1)}%;background:var(--purple)"></div>` : ''}
          ${toolPct > 0.1 ? `<div class="xray-seg invisible" title="Tool output: ${toolPct.toFixed(1)}%" style="width:${toolPct.toFixed(1)}%;background:var(--accent)"></div>` : ''}
          ${systemPct > 0.1 ? `<div class="xray-seg invisible" title="System: ${systemPct.toFixed(1)}%" style="width:${systemPct.toFixed(1)}%;background:var(--pink)"></div>` : ''}
        </div>
        <div class="xray-legend">
          <span><span class="legend-dot" style="background:var(--text-secondary)"></span> Plain text ${fmtCompact(plainTextTokens)}</span>
          ${codeTokens > 0 ? `<span><span class="legend-dot" style="background:var(--cyan)"></span> Code ${fmtCompact(codeTokens)}</span>` : ''}
          ${tableTokens > 0 ? `<span><span class="legend-dot" style="background:var(--yellow)"></span> Tables ${fmtCompact(tableTokens)}</span>` : ''}
          ${totalImageTokens > 0 ? `<span><span class="legend-dot" style="background:var(--purple)"></span> Images ${fmtCompact(totalImageTokens)}</span>` : ''}
          ${totalToolTokens > 0 ? `<span><span class="legend-dot" style="background:var(--accent)"></span> Tool output ${fmtCompact(totalToolTokens)}</span>` : ''}
          ${totalSystemTokens > 0 ? `<span><span class="legend-dot" style="background:var(--pink)"></span> System ${fmtCompact(totalSystemTokens)}</span>` : ''}
        </div>
      </div>

      <div class="metric-grid" style="margin-top:2rem">
        ${totalToolCalls > 0 ? `
        <div class="metric-card">
          <div class="metric-label">Tool calls</div>
          <div class="metric-value">${totalToolCalls.toLocaleString()}</div>
          <div class="metric-detail">${fmtCompact(totalToolTokens)} tokens (browsing, code exec)</div>
        </div>` : ''}
        ${totalSystemTokens > 0 ? `
        <div class="metric-card">
          <div class="metric-label">System overhead</div>
          <div class="metric-value">${fmtCompact(totalSystemTokens)}<span class="unit">tok</span></div>
          <div class="metric-detail">Invisible system messages</div>
        </div>` : ''}
        ${totalCodeBlocks > 0 ? `
        <div class="metric-card">
          <div class="metric-label">Code blocks</div>
          <div class="metric-value">${totalCodeBlocks.toLocaleString()}</div>
          <div class="metric-detail">${fmtCompact(Math.ceil(totalCodeChars / 4))} tokens in fenced code</div>
        </div>` : ''}
        ${totalTables > 0 ? `
        <div class="metric-card">
          <div class="metric-label">Tables</div>
          <div class="metric-value">${totalTables.toLocaleString()}</div>
          <div class="metric-detail">${fmtCompact(Math.ceil(totalTableChars / 4))} tokens in markdown tables</div>
        </div>` : ''}
        ${bloatDeltaConvs > 0 ? `
        <div class="metric-card">
          <div class="metric-label">Hidden bloat</div>
          <div class="metric-value">${bloatDeltaConvs}</div>
          <div class="metric-detail">chats fatter than they appear</div>
        </div>` : ''}
        ${totalGhostWasted > 0 ? `
        <div class="metric-card">
          <div class="metric-label">Ghost zone</div>
          <div class="metric-value">${totalGhostWasted}</div>
          <div class="metric-detail">messages sent into the void</div>
        </div>` : ''}
      </div>

      <div class="subsection">
        <div class="section-label">Conversation content mix</div>
        <div class="stats-row">
          <div>
            <span class="stat-value">${dominantCounts.text}</span>
            <span class="stat-label">Text-dominant</span>
          </div>
          <div>
            <span class="stat-value">${dominantCounts.code}</span>
            <span class="stat-label">Code-heavy</span>
          </div>
          <div>
            <span class="stat-value">${dominantCounts.image}</span>
            <span class="stat-label">Image-heavy</span>
          </div>
          <div>
            <span class="stat-value">${dominantCounts.table}</span>
            <span class="stat-label">Table-heavy</span>
          </div>
        </div>
      </div>

      ${biggestHidden.length > 0 ? `
      <div class="subsection">
        <div class="section-label">Biggest hidden bloat</div>
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr><th>Title</th><th>Visible</th><th>Adjusted</th><th>Delta</th><th>Tools</th><th>System</th></tr>
            </thead>
            <tbody>
              ${biggestHidden.map(c => {
                const rawPct = (c.bloatScore * 100).toFixed(0);
                const adjPct = (c.adjustedBloatScore * 100).toFixed(0);
                const delta = ((c.adjustedBloatScore - c.bloatScore) * 100).toFixed(0);
                return `
              <tr>
                <td class="title-cell">${esc(c.title)}</td>
                <td class="num">${rawPct}%</td>
                <td class="num">${adjPct}%</td>
                <td class="num" style="color:var(--red)">+${delta}%</td>
                <td class="num">${c.contentProfile.toolCallCount || '\u2014'}</td>
                <td class="num">${c.contentProfile.systemTokenEstimate > 0 ? fmtCompact(c.contentProfile.systemTokenEstimate) : '\u2014'}</td>
              </tr>`;
              }).join('')}
            </tbody>
          </table>
        </div>
      </div>
      ` : ''}
    </section>
  `;
}

// ============================================================
// Section: Fattest Chats
// ============================================================

function renderFattestChats(result) {
  if (result.topBloated.length === 0) return '';

  const rows = result.topBloated.map((conv, i) => {
    const score = conv.adjustedBloatScore || conv.bloatScore;
    const barWidth = Math.min(score * 25, 100);
    const wash = score >= 1.0 ? 'wash-5' : score >= 0.75 ? 'wash-4' : score >= 0.5 ? 'wash-3' : score >= 0.25 ? 'wash-2' : 'wash-1';
    const scorePct = (score * 100).toFixed(0);
    const contentType = conv.renderProfile?.dominantContentType || 'text';
    const delta = conv.adjustedBloatScore - conv.bloatScore;
    const deltaTitle = delta > 0.05
      ? `title="+${(delta * 100).toFixed(0)}% hidden overhead from tool calls and system prompts"`
      : '';
    const deltaStr = delta > 0.05
      ? ` <span class="bloat-delta" ${deltaTitle}>+${(delta * 100).toFixed(0)}% hidden</span>`
      : '';

    return `
      <tr class="fattest-row wash-border-${wash.split('-')[1]}" data-conv-idx="${i}">
        <td class="num">${i + 1}</td>
        <td class="title-cell">${esc(conv.title)}</td>
        <td><span class="content-badge ${contentType}">${contentType}</span></td>
        <td>
          <div class="bloat-meter">
            <div class="bloat-meter-track">
              <div class="bloat-meter-fill ${wash}" style="width:${barWidth}%"></div>
            </div>
            <span class="bloat-meter-val" style="color:var(--${wash})">${scorePct}%</span>
          </div>
          ${deltaStr}
        </td>
        <td class="num">${conv.totalTokens.toLocaleString()}</td>
        <td class="num">${conv.messageCount}</td>
        <td class="num">${conv.contentProfile?.toolCallCount || '\u2014'}</td>
        <td class="num">${conv.imageCount || '\u2014'}</td>
        <td class="num">${conv.ghostWastedMessages || '\u2014'}</td>
        <td class="num">${conv.chatTax ? (conv.chatTax.score * 100).toFixed(0) + '%' : '\u2014'}</td>
        <td class="num">${conv.modelSlug || '\u2014'}</td>
      </tr>
      <tr class="detail-row" data-detail-for="${i}" hidden>
        <td colspan="11">${renderConvXray(conv)}</td>
      </tr>
    `;
  }).join('');

  return `
    <section class="section" id="section-fattest">
      <div class="section-label">Fattest Chats</div>
      <div class="section-title">Top ${result.topBloated.length} by bloat score</div>
      <div class="section-desc">Click a row to see its token breakdown.</div>

      <div class="table-wrap">
        <table class="data-table" id="fattest-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Title</th>
              <th>Type</th>
              <th>Bloat</th>
              <th>Tokens</th>
              <th>Msgs</th>
              <th>Tools</th>
              <th>Img</th>
              <th>Wasted</th>
              <th>Tax</th>
              <th>Model</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </section>
  `;
}

/**
 * Render a per-conversation token composition x-ray (for progressive disclosure).
 */
function renderConvXray(conv) {
  const at = conv.adjustedTokens;
  const cp = conv.contentProfile;

  const codeTokens = Math.ceil((cp.codeBlockChars || 0) / 4);
  const tableTokens = Math.ceil((cp.tableChars || 0) / 4);
  const plainTokens = Math.max(0, at.textTokens - codeTokens - tableTokens);
  const total = at.textTokens + at.imageTokens + at.toolCallTokens + at.systemTokens;
  if (total === 0) return '<div class="conv-xray">No token data</div>';

  const pct = (n) => total > 0 ? (n / total * 100) : 0;
  const invisibleTokens = at.toolCallTokens + at.systemTokens;
  const invisiblePct = pct(invisibleTokens);

  const segments = [
    { tokens: plainTokens, color: 'var(--text-secondary)', label: 'Plain text' },
    { tokens: codeTokens, color: 'var(--cyan)', label: 'Code' },
    { tokens: tableTokens, color: 'var(--yellow)', label: 'Tables' },
    { tokens: at.imageTokens, color: 'var(--purple)', label: 'Images' },
    { tokens: at.toolCallTokens, color: 'var(--accent)', label: 'Tool output', invisible: true },
    { tokens: at.systemTokens, color: 'var(--pink)', label: 'System', invisible: true },
  ].filter(s => s.tokens > 0);

  const bar = segments.map(s => {
    const w = pct(s.tokens).toFixed(1);
    const cls = s.invisible ? ' invisible' : '';
    return `<div class="xray-seg${cls}" title="${s.label}: ${w}%" style="width:${w}%;background:${s.color}"></div>`;
  }).join('');

  const legend = segments.map(s =>
    `<span><span class="legend-dot" style="background:${s.color}"></span> ${s.label} ${fmtCompact(s.tokens)}</span>`
  ).join('');

  const ghostInfo = conv.ghostWastedMessages > 0
    ? `<div class="conv-xray-stat"><span class="stat-label">Ghost zone</span> <span class="stat-value">${conv.ghostWastedMessages} msgs unheard (starts at msg ${conv.ghostZoneStartIndex})</span></div>`
    : '';

  const contextWindow = conv.contextWindow?.nominal || 128000;
  const effectiveWindow = conv.contextWindow?.effective || 80000;

  return `
    <div class="conv-xray">
      ${invisiblePct > 1 ? `
      <div class="conv-xray-callout">
        <strong>${invisiblePct.toFixed(0)}%</strong> of this chat's context is invisible overhead
        (${fmtCompact(invisibleTokens)} tokens)
      </div>` : ''}
      <div class="xray-bar-wrap" style="margin:0.75rem 0">
        <div class="xray-bar" style="height:22px;border-radius:6px">${bar}</div>
        <div class="xray-legend" style="margin-top:0.5rem">${legend}</div>
      </div>
      <div class="conv-xray-stats">
        <div class="conv-xray-stat"><span class="stat-label">Context window</span> <span class="stat-value">${fmtCompact(contextWindow)} nominal / ${fmtCompact(effectiveWindow)} effective</span></div>
        <div class="conv-xray-stat"><span class="stat-label">Fill rate</span> <span class="stat-value">${((total / effectiveWindow) * 100).toFixed(0)}% of effective window</span></div>
        ${cp.toolCallCount > 0 ? `<div class="conv-xray-stat"><span class="stat-label">Tool calls</span> <span class="stat-value">${cp.toolCallCount} calls (${fmtCompact(at.toolCallTokens)} tokens)</span></div>` : ''}
        ${cp.codeBlockCount > 0 ? `<div class="conv-xray-stat"><span class="stat-label">Code blocks</span> <span class="stat-value">${cp.codeBlockCount} blocks (${fmtCompact(codeTokens)} tokens)</span></div>` : ''}
        ${cp.tableCount > 0 ? `<div class="conv-xray-stat"><span class="stat-label">Tables</span> <span class="stat-value">${cp.tableCount} tables (${fmtCompact(tableTokens)} tokens)</span></div>` : ''}
        ${conv.imageCount > 0 ? `<div class="conv-xray-stat"><span class="stat-label">Images</span> <span class="stat-value">${conv.imageCount} (${conv.userImageCount || 0} uploaded, ${conv.generatedImageCount || 0} generated)</span></div>` : ''}
        ${ghostInfo}
      </div>
    </div>
  `;
}

// ============================================================
// Section: Ghost Waste
// ============================================================

function renderGhostWaste(result) {
  const totalGhostWasted = result.conversations.reduce((s, c) => s + (c.ghostWastedMessages || 0), 0);
  const totalContextWasted = result.crossChat?.totalWastedMessages || 0;
  const avgWasteRate = result.crossChat?.avgContextWasteRate || 0;
  const ghostConvs = result.conversations.filter(c => c.ghostWastedMessages > 0).length;
  const wasteConvs = result.conversations.filter(c => c.contextWaste && c.contextWaste.wastedMessageCount > 0).length;

  if (totalGhostWasted === 0 && totalContextWasted === 0) return '';

  // Top conversations by ghost waste
  const topGhost = result.conversations
    .filter(c => c.ghostWastedMessages > 0)
    .sort((a, b) => b.ghostWastedMessages - a.ghostWastedMessages)
    .slice(0, 5);

  // Top conversations by context waste rate
  const topContextWaste = result.conversations
    .filter(c => c.contextWaste && c.contextWaste.contextWasteRate > 0.1)
    .sort((a, b) => b.contextWaste.contextWasteRate - a.contextWaste.contextWasteRate)
    .slice(0, 5);

  return `
    <section class="section" id="section-waste">
      <div class="section-label">The Void</div>
      <div class="section-title">Messages ChatGPT never heard</div>
      <div class="section-desc">
        Once a chat overflows the context window, your messages still get responses &mdash;
        but the model can no longer see your earlier instructions. You're talking to something
        that's already forgotten the conversation.
      </div>

      <div class="metric-grid">
        ${totalGhostWasted > 0 ? `
        <div class="metric-card">
          <div class="metric-label">Ghost zone messages</div>
          <div class="metric-value">${totalGhostWasted.toLocaleString()}</div>
          <div class="metric-detail">sent after context overflow in ${ghostConvs} chats</div>
        </div>` : ''}
        ${totalContextWasted > 0 ? `
        <div class="metric-card">
          <div class="metric-label">Unheard messages</div>
          <div class="metric-value">${totalContextWasted.toLocaleString()}</div>
          <div class="metric-detail">no echo in ${wasteConvs} conversations</div>
        </div>` : ''}
        ${avgWasteRate > 0 ? `
        <div class="metric-card">
          <div class="metric-label">Avg void rate</div>
          <div class="metric-value">${(avgWasteRate * 100).toFixed(1)}<span class="unit">%</span></div>
          <div class="metric-detail">of user messages go unheard</div>
        </div>` : ''}
      </div>

      ${topGhost.length > 0 ? `
      <div class="subsection">
        <div class="section-label">Most messages into the void</div>
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr><th>Title</th><th>Ghost msgs</th><th>Void rate</th><th>Total msgs</th><th>Model</th></tr>
            </thead>
            <tbody>
              ${topGhost.map(c => `
                <tr>
                  <td class="title-cell">${esc(c.title)}</td>
                  <td class="num">${c.ghostWastedMessages}</td>
                  <td class="num">${(c.ghostWasteRate * 100).toFixed(0)}%</td>
                  <td class="num">${c.messageCount}</td>
                  <td class="num">${c.modelSlug || '\u2014'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>
      ` : ''}

      ${topContextWaste.length > 0 ? `
      <div class="subsection">
        <div class="section-label">Most unheard messages</div>
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr><th>Title</th><th>Unheard</th><th>Unheard rate</th><th>User msgs</th></tr>
            </thead>
            <tbody>
              ${topContextWaste.map(c => `
                <tr>
                  <td class="title-cell">${esc(c.title)}</td>
                  <td class="num">${c.contextWaste.wastedMessageCount}</td>
                  <td class="num">${(c.contextWaste.contextWasteRate * 100).toFixed(0)}%</td>
                  <td class="num">${c.contextWaste.userMessageCount}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>
      ` : ''}
    </section>
  `;
}

// ============================================================
// Section: Cross-Chat Patterns
// ============================================================

function renderPatterns(result) {
  const cc = result.crossChat;

  const affectedCount = result.conversations
    .filter(c => c.reinstruction && c.reinstruction.reinstructionCount > 0).length;

  // Top reinstruction conversations
  const topRe = result.conversations
    .filter(c => c.reinstruction && c.reinstruction.reinstructionCount > 0)
    .sort((a, b) => b.reinstruction.reinstructionRate - a.reinstruction.reinstructionRate)
    .slice(0, 5);

  // Top restart groups
  const topGroups = cc.topicRestarts.restartGroups.slice(0, 5);

  return `
    <section class="section" id="section-patterns">
      <div class="section-label">Bad Habits</div>
      <div class="section-title">Cross-chat patterns</div>

      <div class="metric-grid" style="margin-bottom:2rem">
        <div class="metric-card">
          <div class="metric-label">Re-instruction rate</div>
          <div class="metric-value">${(cc.avgReinstructionRate * 100).toFixed(1)}<span class="unit">%</span></div>
          <div class="metric-detail">${affectedCount} chats affected</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Topic restarts</div>
          <div class="metric-value">${cc.topicRestarts.totalRestartGroups}</div>
          <div class="metric-detail">${cc.topicRestarts.totalRestartConversations} chats in groups</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Orphaned chats</div>
          <div class="metric-value">${cc.orphanedChats.orphanedCount}</div>
          <div class="metric-detail">${cc.orphanedChats.singleExchange} single exchange</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Stale returns</div>
          <div class="metric-value">${cc.totalStaleReturns}</div>
          <div class="metric-detail">${cc.totalLongGaps} long gaps (2-24h)</div>
        </div>${(() => {
          const imgTotal = result.conversations.reduce((s, c) => s + (c.imageTokens || 0), 0);
          if (imgTotal === 0) return '';
          const imgPct = result.totalTokens > 0 ? (imgTotal / result.totalTokens * 100).toFixed(1) : '0.0';
          return `
        <div class="metric-card">
          <div class="metric-label">Image token share</div>
          <div class="metric-value">${imgPct}<span class="unit">%</span></div>
          <div class="metric-detail">${fmtCompact(imgTotal)} vision tokens</div>
        </div>`;
        })()}
      </div>

      ${topRe.length > 0 ? `
        <div class="subsection">
          <div class="section-label">Highest re-instruction rates</div>
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr><th>Title</th><th>Rate</th><th>Flags</th><th>User msgs</th></tr>
              </thead>
              <tbody>
                ${topRe.map(c => `
                  <tr>
                    <td class="title-cell">${esc(c.title)}</td>
                    <td class="num">${(c.reinstruction.reinstructionRate * 100).toFixed(0)}%</td>
                    <td class="num">${c.reinstruction.reinstructionCount}</td>
                    <td class="num">${c.reinstruction.userMessageCount}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </div>
      ` : ''}

      ${topGroups.length > 0 ? `
        <div class="subsection">
          <div class="section-label">Top restart groups</div>
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr><th>Signal</th><th>Key</th><th>Chats</th></tr>
              </thead>
              <tbody>
                ${topGroups.map(g => `
                  <tr>
                    <td>${esc(g.signal)}</td>
                    <td class="title-cell">${esc(g.key)}</td>
                    <td class="num">${g.count}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </div>
      ` : ''}

      <div class="subsection">
        <div class="section-label">Orphaned chat breakdown</div>
        <div class="stats-row">
          <div>
            <span class="stat-value">${cc.orphanedChats.singleExchange}</span>
            <span class="stat-label">Single exchange</span>
          </div>
          <div>
            <span class="stat-value">${cc.orphanedChats.abandonedStart}</span>
            <span class="stat-label">Abandoned starts</span>
          </div>
          <div>
            <span class="stat-value">${cc.orphanedChats.orphanedWithGizmo}</span>
            <span class="stat-label">With shared GPT</span>
          </div>
        </div>
      </div>

      <div class="subsection">
        <div class="section-label">Restart groups by type</div>
        <div class="stats-row">
          <div>
            <span class="stat-value">${cc.topicRestarts.gizmoRestartGroups}</span>
            <span class="stat-label">GPT-based groups</span>
          </div>
          <div>
            <span class="stat-value">${cc.topicRestarts.titleRestartGroups}</span>
            <span class="stat-label">Title-based groups</span>
          </div>
        </div>
      </div>
    </section>
  `;
}

// ============================================================
// Section: Perceived Wait
// ============================================================

function renderPerceivedWait(result) {
  // Archive-wide median (median of per-conversation medians)
  const convWaits = result.conversations
    .filter(c => c.perceivedWait && c.perceivedWait.medianPerceivedWait != null)
    .map(c => c.perceivedWait.medianPerceivedWait)
    .sort((a, b) => a - b);
  const archiveMedian = medianOf(convWaits);

  // Top 5 by total perceived wait
  const topWait = result.conversations
    .filter(c => c.perceivedWait && c.perceivedWait.totalPerceivedWaitMinutes > 0)
    .sort((a, b) => b.perceivedWait.totalPerceivedWaitMinutes - a.perceivedWait.totalPerceivedWaitMinutes)
    .slice(0, 5);

  // TTFB coverage
  let turnsWithTTFB = 0;
  let totalTurns = 0;
  for (const conv of result.conversations) {
    for (const tp of conv.turnPairs) {
      totalTurns++;
      if (tp.ttfb != null) turnsWithTTFB++;
    }
  }

  return `
    <section class="section" id="section-wait">
      <div class="section-label">The Waiting Game</div>
      <div class="section-title">Perceived wait analysis</div>

      <div class="metric-grid" style="margin-bottom:2rem">
        <div class="metric-card">
          <div class="metric-label">Median perceived wait</div>
          <div class="metric-value">${fmt(archiveMedian, 1)}<span class="unit">s</span></div>
          <div class="metric-detail">per response, archive-wide</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">TTFB coverage</div>
          <div class="metric-value">${totalTurns > 0 ? ((turnsWithTTFB / totalTurns) * 100).toFixed(0) : 0}<span class="unit">%</span></div>
          <div class="metric-detail">${turnsWithTTFB.toLocaleString()} of ${totalTurns.toLocaleString()} turns</div>
        </div>
      </div>

      ${topWait.length > 0 ? `
        <div class="section-label">Highest total perceived wait</div>
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr><th>Title</th><th>Total</th><th>Median</th><th>Drag</th><th>Turns</th></tr>
            </thead>
            <tbody>
              ${topWait.map(c => `
                <tr>
                  <td class="title-cell">${esc(c.title)}</td>
                  <td class="num">${c.perceivedWait.totalPerceivedWaitMinutes.toFixed(0)} min</td>
                  <td class="num">${fmt(c.perceivedWait.medianPerceivedWait, 1)}s</td>
                  <td class="num">${fmt(c.perceivedWait.perceivedDragRatio, 1)}x</td>
                  <td class="num">${c.turnPairs.length}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      ` : ''}
    </section>
  `;
}

// ============================================================
// Section: Archive Overview
// ============================================================

function renderOverview(result) {
  const modelEntries = Object.entries(result.modelDistribution)
    .sort((a, b) => b[1] - a[1]);

  const gizmoCount = Object.keys(result.gizmoInventory).length;

  // Use adjusted total (text + images + tool calls + system) for consistency with X-Ray
  const adjustedTotalTokens = result.conversations.reduce((s, c) => {
    const at = c.adjustedTokens;
    return s + at.textTokens + at.imageTokens + at.toolCallTokens + at.systemTokens;
  }, 0);

  // Aggregate image metrics across all conversations
  const totalImages = result.conversations.reduce((s, c) => s + (c.imageCount || 0), 0);
  const totalImageTokens = result.conversations.reduce((s, c) => s + (c.imageTokens || 0), 0);
  const totalUserImages = result.conversations.reduce((s, c) => s + (c.userImageCount || 0), 0);
  const totalGenImages = result.conversations.reduce((s, c) => s + (c.generatedImageCount || 0), 0);
  const imageTokenPct = adjustedTotalTokens > 0 ? (totalImageTokens / adjustedTotalTokens * 100).toFixed(1) : '0.0';

  return `
    <section class="section" id="section-overview">
      <div class="section-label">By the Numbers</div>
      <div class="section-title">Archive overview</div>

      <div class="metric-grid">
        <div class="metric-card">
          <div class="metric-label">Total chats</div>
          <div class="metric-value">${result.totalConversations.toLocaleString()}</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Total messages</div>
          <div class="metric-value">${result.totalMessages.toLocaleString()}</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Estimated tokens</div>
          <div class="metric-value">${fmtCompact(adjustedTotalTokens)}</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Archive size</div>
          <div class="metric-value">${(result.archiveSizeBytes / (1024 * 1024)).toFixed(0)}<span class="unit">MB</span></div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Archive span</div>
          <div class="metric-value">${Math.round(result.chatgptTax.archiveSpanDays)}<span class="unit">days</span></div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Custom GPTs used</div>
          <div class="metric-value">${gizmoCount}</div>
        </div>${totalImages > 0 ? `
        <div class="metric-card">
          <div class="metric-label">Images</div>
          <div class="metric-value">${totalImages.toLocaleString()}</div>
          <div class="metric-detail">${totalUserImages} uploaded / ${totalGenImages} generated</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Image tokens</div>
          <div class="metric-value">${fmtCompact(totalImageTokens)}</div>
          <div class="metric-detail">${imageTokenPct}% of total</div>
        </div>` : ''}
      </div>

      ${modelEntries.length > 0 ? `
        <div class="subsection">
          <div class="section-label">Model distribution</div>
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr><th>Model</th><th>Chats</th><th>Share</th></tr>
              </thead>
              <tbody>
                ${modelEntries.map(([slug, count]) => `
                  <tr>
                    <td>${esc(slug)}</td>
                    <td class="num">${count}</td>
                    <td class="num">${(count / result.totalConversations * 100).toFixed(0)}%</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </div>
      ` : ''}
    </section>
  `;
}

// ============================================================
// Section: Raw Data
// ============================================================

function renderRawData(result) {
  // Compact JSON — omit messages array detail
  const json = JSON.stringify(result, (key, val) => {
    if (key === 'messages') return `[${val.length} messages]`;
    if (key === 'turnPairs') return `[${val.length} turn pairs]`;
    if (val === Infinity) return 'Infinity';
    return val;
  }, 2);

  return `
    <section class="section visible" id="section-raw">
      <details class="raw-toggle">
        <summary>Raw analysis data (JSON)</summary>
        <pre class="raw-json">${esc(json)}</pre>
      </details>
    </section>
  `;
}

// ============================================================
// Utilities
// ============================================================

/** Escape HTML entities */
function esc(str) {
  const el = document.createElement('div');
  el.textContent = str || '';
  return el.innerHTML;
}

/** Format a number, returning em-dash for null/Infinity */
function fmt(n, decimals = 1) {
  if (n == null || !isFinite(n)) return '\u2014';
  return n.toFixed(decimals);
}

/** Format minutes as human-readable string */
function fmtMin(min) {
  if (min >= 120) return `${(min / 60).toFixed(1)} hrs`;
  if (min >= 1) return `${Math.round(min)} min`;
  return '<1 min';
}

/** Format minutes as HTML with unit span */
function fmtTimeHtml(min) {
  if (min >= 120) {
    return `${(min / 60).toFixed(1)}<span class="unit">hrs</span>`;
  }
  if (min >= 1) {
    return `${Math.round(min)}<span class="unit">min</span>`;
  }
  return `&lt;1<span class="unit">min</span>`;
}

/** Format large numbers compactly */
function fmtCompact(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}<span class="unit">M</span>`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}<span class="unit">K</span>`;
  return n.toLocaleString();
}

/** Calculate median of a sorted array */
function medianOf(arr) {
  if (arr.length === 0) return null;
  const mid = Math.floor(arr.length / 2);
  return arr.length % 2 ? arr[mid] : (arr[mid - 1] + arr[mid]) / 2;
}

// ============================================================
// Animations
// ============================================================

function initAnimations() {
  // Counter animation for hero number
  const heroEl = document.querySelector('[data-counter]');
  if (heroEl) {
    const target = parseFloat(heroEl.dataset.counter);
    animateCounter(heroEl, target);
  }

  // Intersection observer for section reveals
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        observer.unobserve(entry.target);
      }
    }
  }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

  document.querySelectorAll('.section:not(.visible)').forEach((el) => {
    observer.observe(el);
  });

  // Make the first section visible immediately
  const firstSection = document.querySelector('.section');
  if (firstSection) {
    firstSection.classList.add('visible');
  }

  // Fattest chats row expansion
  initFattestRowExpansion();
}

function initFattestRowExpansion() {
  const table = document.getElementById('fattest-table');
  if (!table) return;

  table.addEventListener('click', (e) => {
    const row = e.target.closest('.fattest-row');
    if (!row) return;

    const idx = row.dataset.convIdx;
    const detailRow = table.querySelector(`[data-detail-for="${idx}"]`);
    if (!detailRow) return;

    const isOpen = !detailRow.hidden;
    // Close all other open detail rows
    table.querySelectorAll('.detail-row:not([hidden])').forEach(r => {
      r.hidden = true;
      const parentIdx = r.dataset.detailFor;
      table.querySelector(`.fattest-row[data-conv-idx="${parentIdx}"]`)?.classList.remove('expanded');
    });

    if (!isOpen) {
      detailRow.hidden = false;
      row.classList.add('expanded');
    }
  });
}

function animateCounter(el, target, duration = 1800) {
  const start = performance.now();
  const decimals = target >= 100 ? 0 : 1;

  const update = (now) => {
    const progress = Math.min((now - start) / duration, 1);
    // Ease-out cubic
    const eased = 1 - Math.pow(1 - progress, 3);
    const current = target * eased;

    el.textContent = current.toFixed(decimals);

    if (progress < 1) {
      requestAnimationFrame(update);
    } else {
      el.textContent = target.toFixed(decimals);
    }
  };

  requestAnimationFrame(update);
}
